import type { Customer, Operation, User } from '@gmn-portal/backend';
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Eye, MoreHorizontal, Plus, Wrench } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useDeleteOperation, useOperations, type Where } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  SortableHeader,
  TableActiveFilterTags,
  TableCard,
  TableCardContent,
  TableCardFooter,
  TableCardHeader,
  TableLoadingCell,
  TablePagination,
  TableSearchBar,
} from '@/shared/components/tables';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useTablePreferences } from '@/shared/services/table-preferences';
import { canCreateService, canDeleteService, resolveUserGroup } from '@/shared/utils/permissions';
import { getSortParam } from '@/shared/utils/table-utils';

type OverviewMode = 'gmn' | 'customer';

const buildLikeWhere = (field: string, value: string): Where => ({
  [field]: { like: value },
});

const getCustomerName = (customer: Operation['customer']) => {
  if (!customer) return '-';
  if (typeof customer === 'string') return customer;
  return (customer as Customer).name || '-';
};

const getUserLabel = (u: Operation['responsibleGmnUser'] | Operation['assignedCustomerUser']) => {
  if (!u) return '-';
  if (typeof u === 'string') return u;
  const user = u as User;
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return fullName || user.id;
};

const StatusBadge = ({ status }: { status: Operation['status'] }) => {
  const { t } = useIntl();
  if (status === 'closed') {
    return <Badge variant="outline-solid">{t('operations.status.closed')}</Badge>;
  }
  return (
    <Badge variant="outline-solid" className="text-green-600">
      {t('operations.status.open')}
    </Badge>
  );
};

const DecisionBadge = ({ decision }: { decision: Operation['followUpDecision'] }) => {
  const { t } = useIntl();
  switch (decision) {
    case 'yes':
      return (
        <Badge variant="outline-solid" className="text-green-600">
          {t('operations.followUpDecision.yes')}
        </Badge>
      );
    case 'no':
      return (
        <Badge variant="outline-solid" className="text-red-600">
          {t('operations.followUpDecision.no')}
        </Badge>
      );
    default:
      return (
        <Badge variant="outline-solid" className="text-yellow-600">
          {t('operations.followUpDecision.pending')}
        </Badge>
      );
  }
};

const ActionCell = ({
  operationId,
  onView,
  onDelete,
}: {
  operationId: string;
  onView: (id: string) => void;
  onDelete?: (id: string) => void;
}) => {
  const { t } = useIntl();
  return (
    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onView(operationId);
        }}
        className="p-1.5 hover:bg-secondary text-primary rounded-md transition-colors"
        aria-label={t('common.view')}
        title={t('common.view')}
      >
        <Eye size={16} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(event) => event.stopPropagation()}
            className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
            aria-label={t('navigation.openMenu')}
            title={t('navigation.openMenu')}
          >
            <MoreHorizontal size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => onView(operationId)}>
            {t('common.view')}
          </DropdownMenuItem>
          {onDelete ? (
            <DropdownMenuItem
              onClick={() => onDelete(operationId)}
              className="text-destructive focus:text-destructive"
            >
              {t('common.delete')}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export function OperationsOverview({ mode = 'gmn' }: { mode?: OverviewMode }) {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();

  const group = resolveUserGroup(user);
  const canCreate = group ? canCreateService(group, 'operations') : false;
  const canDelete = group ? canDeleteService(group, 'operations') : false;
  const basePath = mode === 'gmn' ? '/gmn/operations' : '/c/my-operations';

  const tablePreferences = useTablePreferences({
    tableId: mode === 'gmn' ? 'operations-overview-gmn' : 'operations-overview-customer',
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting: [{ id: 'createdAt', desc: true }],
      columnFilters: [],
      columnVisibility: {},
      searchTerm: '',
    },
  });

  const preferences = tablePreferences.loadPreferences();
  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    preferences.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    preferences.columnVisibility || {}
  );
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 10,
    }
  );

  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      searchTerm,
    });
  }, [pagination, sorting, columnFilters, columnVisibility, searchTerm, tablePreferences]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const activeFilterTags = searchTerm
    ? [
        {
          id: 'search',
          label: (
            <>
              <span className="font-medium text-foreground">{t('common.searchLabel')}:</span>
              <span className="truncate max-w-[240px]" title={searchTerm}>
                {searchTerm}
              </span>
            </>
          ),
          onRemove: () => handleSearchChange(''),
        },
      ]
    : [];

  const handleViewDetail = useCallback(
    (id: string) => {
      navigate(`${basePath}/${id}`);
    },
    [navigate, basePath]
  );

  const columns: ColumnDef<Operation>[] = useMemo(() => {
    const base: ColumnDef<Operation>[] = [
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <SortableHeader column={column} title={t('common.createdAt')} />,
        cell: ({ row }) => {
          const date = row.getValue('createdAt') as string;
          return (
            <span className="text-muted-foreground">
              {date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy HH:mm') : '-'}
            </span>
          );
        },
        size: 150,
        minSize: 140,
        maxSize: 200,
      },
      {
        accessorKey: 'title',
        header: ({ column }) => (
          <SortableHeader column={column} title={t('operations.fields.title')} />
        ),
        cell: ({ row }) => (
          <span className="text-foreground font-medium">{row.getValue('title') as string}</span>
        ),
        size: 260,
        minSize: 180,
        maxSize: 340,
      },
      ...(mode === 'gmn'
        ? ([
            {
              id: 'customer',
              header: ({ column }) => (
                <SortableHeader column={column} title={t('customers.customer')} />
              ),
              accessorFn: (row) => getCustomerName(row.customer),
              cell: ({ row }) => (
                <span className="text-foreground font-medium">
                  {getCustomerName(row.original.customer)}
                </span>
              ),
              size: 250,
              minSize: 180,
              maxSize: 320,
            },
            {
              id: 'assignedCustomerUser',
              header: ({ column }) => (
                <SortableHeader
                  column={column}
                  title={t('operations.fields.assignedCustomerUser')}
                />
              ),
              accessorFn: (row) => getUserLabel(row.assignedCustomerUser),
              cell: ({ row }) => (
                <span className="text-muted-foreground">
                  {getUserLabel(row.original.assignedCustomerUser)}
                </span>
              ),
              size: 260,
              minSize: 180,
              maxSize: 340,
            },
          ] as ColumnDef<Operation>[])
        : []),
      {
        accessorKey: 'followUpDecision',
        header: ({ column }) => (
          <SortableHeader column={column} title={t('operations.fields.followUpDecision')} />
        ),
        cell: ({ row }) => <DecisionBadge decision={row.getValue('followUpDecision') as any} />,
        size: 160,
        minSize: 140,
        maxSize: 220,
      },
      {
        accessorKey: 'status',
        header: ({ column }) => (
          <SortableHeader column={column} title={t('operations.fields.status')} />
        ),
        cell: ({ row }) => <StatusBadge status={row.getValue('status') as any} />,
        size: 140,
        minSize: 120,
        maxSize: 180,
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{t('common.actions')}</span>,
        cell: ({ row }) => (
          <ActionCell
            operationId={row.original.id}
            onView={handleViewDetail}
            onDelete={mode === 'gmn' && canDelete ? setOperationIdToDelete : undefined}
          />
        ),
        size: 80,
        minSize: 60,
        maxSize: 100,
      },
    ];
    return base;
  }, [t, mode, handleViewDetail, canDelete]);

  const where = debouncedSearchTerm
    ? {
        or: [
          buildLikeWhere('id', debouncedSearchTerm),
          buildLikeWhere('title', debouncedSearchTerm),
          buildLikeWhere('description', debouncedSearchTerm),
          buildLikeWhere('customer.name', debouncedSearchTerm),
          buildLikeWhere('assignedCustomerUser.email', debouncedSearchTerm),
          buildLikeWhere('responsibleGmnUser.email', debouncedSearchTerm),
        ],
      }
    : undefined;

  const { data: operationsData, isLoading } = useOperations({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    sort: getSortParam<Operation>(sorting),
    depth: 1,
    where,
  });

  const operations = operationsData?.docs || [];
  const totalPages = operationsData?.totalPages || 0;
  const totalDocs = operationsData?.totalDocs || 0;

  const [operationIdToDelete, setOperationIdToDelete] = useState<string | null>(null);
  const deleteOperation = useDeleteOperation();

  const handleDelete = async () => {
    if (!operationIdToDelete) return;
    try {
      await deleteOperation.mutateAsync(operationIdToDelete);
      toast.success(t('operations.toasts.deleted'));
      setOperationIdToDelete(null);
    } catch {
      toast.error(t('operations.toasts.deleteFailed'));
    }
  };

  const table = useReactTable({
    data: operations,
    columns,
    pageCount: totalPages,
    rowCount: totalDocs,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  return (
    <PageContainer>
      <PageTitle
        icon={Wrench}
        breadcrumb={
          mode === 'gmn' ? (
            <>
              <SimpleBreadcrumbItem>{t('navigation.items.gmnOperations')}</SimpleBreadcrumbItem>
              <SimpleBreadcrumbItem isLast>
                {t('navigation.items.allOperations')}
              </SimpleBreadcrumbItem>
            </>
          ) : (
            <>
              <SimpleBreadcrumbItem>
                {t('navigation.items.customerOperations')}
              </SimpleBreadcrumbItem>
              <SimpleBreadcrumbItem isLast>
                {t('navigation.items.myOperations')}
              </SimpleBreadcrumbItem>
            </>
          )
        }
        title={mode === 'gmn' ? t('operations.manageOperations') : t('operations.myOperations')}
        primaryAction={
          mode === 'gmn'
            ? {
                label: t('operations.addOperation'),
                onClick: () => navigate('/gmn/operations/new'),
                icon: <Plus size={16} />,
                disabled: !canCreate,
              }
            : undefined
        }
      />

      <TableCard className="mb-4">
        <TableCardHeader>
          <TableSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder={t('operations.searchPlaceholder')}
          />
        </TableCardHeader>
      </TableCard>

      {activeFilterTags.length > 0 ? (
        <div className="mb-4">
          <TableActiveFilterTags tags={activeFilterTags} />
        </div>
      ) : null}

      <TableCard>
        <TableCardContent>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableHeaderRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <TableHead
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                      className={
                        index === 0
                          ? 'pl-6'
                          : index === headerGroup.headers.length - 1
                            ? 'pr-6 text-right'
                            : ''
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableHeaderRow>
              ))}
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableLoadingCell colSpan={columns.length} />
              ) : operations.length === 0 ? (
                <TableRow className="hover:bg-card">
                  <TableCell
                    colSpan={columns.length}
                    className="p-8 text-center text-muted-foreground text-sm"
                  >
                    {t('operations.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="group cursor-pointer hover:bg-muted/50"
                    onClick={() => handleViewDetail(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}px` }}
                        className={
                          index === 0
                            ? 'pl-6'
                            : index === row.getVisibleCells().length - 1
                              ? 'pr-6 text-right'
                              : ''
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableCardContent>

        <TableCardFooter>
          <TablePagination table={table} />
        </TableCardFooter>
      </TableCard>

      <AlertDialog
        open={!!operationIdToDelete}
        onOpenChange={(open) => !open && setOperationIdToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('operations.actions.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('operations.actions.deleteConfirmDescription', {
                title:
                  operations.find((o) => o.id === operationIdToDelete)?.title ||
                  operationIdToDelete ||
                  '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              disabled={deleteOperation.isPending}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
